import Vue from 'vue'
import { t } from 'i18next'
/**
 * Debounces a function and prevents outdated async results from being used.
 * 
 * @param {Function} func 
 * @param {Number} wait 
 * @param {Boolean} immediate 
 */
var debounce = function(func, wait, immediate, context) {
    let timeout = null;
    let generation = 0;
    return function() {
        context = context || this;
        const args = arguments;
        const callId = ++generation;
        clearTimeout(timeout);
        return new Promise((resolve, reject) => {
            timeout = setTimeout(() => {
                if (callId !== generation) return;
                Promise.resolve(func.apply(context, args)).then(
                    (result) => { if (callId === generation) resolve(result); },
                    (err) => { if (callId === generation) reject(err); }
                );
            }, wait);
        });
    };
};
debounce.timeout = null;
debounce.clearDebounce = ()=>{
    clearTimeout(debounce.timeout);
};

/**
 * @param {String} baseStr 
 * @param {String} maskStr 
 * @param {String} char 
 */
const insertChar = (baseStr, maskStr) => {
    if(baseStr === undefined || baseStr === null){
        return undefined;
    }
    var newStr = "";
    var offset = 0;

    for (var i = 0; i < maskStr.length && i < baseStr.length + offset; i++) {
        if (maskStr[i] === ".") {
            newStr += ".";
            offset++;
        } else {
            newStr += baseStr[i - offset];
        }
    }
    return newStr;
};

const maskFactory = function (maskStr) {
    return function (o) {
        const value = o.value;
        return insertChar(value, maskStr);
    }
};

const showConfirmationDialog = function(modal, wrappedCallback = () => {}, text = "?", confirm = "Sim", denial = "Não"){
    modal.show({
        template:`
            <div class="confirmation-dialog-box">
                <h5 class="title"> ${text} </h5>
                <div class="dialog-button-container">
                    <button class="confirmation-dialog-button" @click="onModalConfirm"> ${confirm} </button>
                    <button class="confirmation-dialog-button" @click="close(false)"> ${denial} </button>
                </div>    
            </div>
        `,
        methods:{
            onModalConfirm(){
                if(typeof wrappedCallback == 'function') {
                    wrappedCallback();
                } else if(typeof wrappedCallback == 'object' && typeof wrappedCallback.onModalConfirm == 'function') {
                    const { onModalConfirm } = wrappedCallback;
                    onModalConfirm();
                }
                this.close(true);
            },
            close(confirmed){
                this.$emit('close');
                if(typeof wrappedCallback == 'object' && typeof wrappedCallback.onModalClose == 'function'){
                    const { onModalClose } = wrappedCallback;
                    onModalClose(confirmed);
                }
            }
        }
    }, {}, {
        height:'auto',
        width:'325px',
    });
};

let isInvalidNumber = (value)=> {
    return value == null || value == undefined  || value == NaN;
};

/**
 * @param {Array<Object>} array 
 * @param {Array<String>} keys 
 */
const sortBy = function(array = [], keys = []) {
    return array.sort((a, b) => {
        for (var i = 0; i < keys.length; i++) {
            const k = keys[i];
            let diff = a[k] - b[k];
            if(isNaN(diff)){
                const subjectA = !!a[k] && a[k] || '';
                const subjectB = !!b[k] && b[k] || '';
                diff = subjectA.localeCompare(subjectB);
            }
            if(diff !== 0){
                return diff;
            }
        }
        return 0;
    });
};

export default {
    wordsPerMinutes: 160,
    /**
     * Return filterData from sessionStorage
     * @param { String | null } name
     */
    getFilterInSession(){
        if(sessionStorage.length == 0 || sessionStorage.getItem('filterData') == null){
            let backOfficeData = this.getLocalVar('backOfficeData');
            if(backOfficeData) {
                sessionStorage.setItem('filterData', JSON.stringify(backOfficeData));
            }
            return backOfficeData || {};
        }
        return JSON.parse(sessionStorage.getItem('filterData') || '{}');
    },
    setFilterInSession(name, value){
        let filterData = this.getFilterInSession();
        filterData[name] = value;
        sessionStorage.setItem('filterData', JSON.stringify(filterData));
    },
    /**
     * Return values stored into localStorage.
     * 
     * @param {String} varName 
     */
    getLocalVar(varName){
        return JSON.parse(localStorage.getItem(varName));
    },

    /**
     * Store values into localStorage.
     * 
     * @param {String} varName 
     * @param {*} value 
     */
    setLocalVar(varName, value){
        localStorage.setItem(varName, JSON.stringify(value));
    },
    
    /**
     * Store values into 'backOfficeData' object,
     * stored into localStorage.
     * 
     * @param {String} dataName 
     * @param {*} dataValue 
     */
    setBackOfficeData(dataName, dataValue){
        let boData = this.getLocalVar('backOfficeData');
        boData[dataName] = dataValue;
        this.setLocalVar('backOfficeData', boData);
    },

    /**
     * Return values from 'backOfficeData' object,
     * stored into localStorage.
     * 
     * @param {String} dataName 
     * @param {*} dataValue 
     */
    getBackOfficeData(dataName){
        if(this.getLocalVar('backOfficeData') && this.getLocalVar('backOfficeData')[dataName] === 0){
            return '0';
        }
        return this.getLocalVar('backOfficeData')[dataName] || null;
    },

    /**
     * Show information notification
     * 
     * @param {Object} message 
     */
    showNotification(message){
        Vue.notify({
            group: 'main',
            title: message.title,
            text: message.text
        });
    },

    /**
     * Show warning notification
     * 
     * @param {Object} message 
     */
    showWarningNotification(message){
        Vue.notify({
            group: 'main',
            type:'warn',
            title: message.title,
            text: message.text
        });
    },

    /**
     * Show error notification
     * 
     * @param {Object} message 
     */
    showErrorNotification(message){
        let params = {
            group: 'main',
            type:'error',
            title: message.title,
            text: message.text
        }
        if(typeof message.duration == 'number') params.duration = message.duration;
        if(typeof message.duration == 'string' && message.duration == 'dynamic'){
            let wordsNumber = (message.title+message.text).split(' ').length;
            let wordsPerSec = this.wordsPerMinutes/60;
            let seconds = wordsNumber / wordsPerSec;
            let milliseconds = seconds * 1000;
            params.duration = milliseconds;
        }
        Vue.notify(params);
    },
    showErrorNotificationFixed(message){
        Vue.notify({
                group: 'main',
                type: 'error',
                title: message.title,
                text: message.text,
                duration: -1
        });
    },

    /**
     * Show success notification
     * 
     * @param {Object} message 
     */
    showSuccessNotification(message){
        Vue.notify({
            group: 'main',
            type:'success',
            title: message.title,
            text: message.text,
        });
    },

    getPlateNivel(){
        return this.getFilterInSession().CDPICTPRAT.match(/\./g).length+1
    },

    getGridColDefsToColDetails(colDefs = []){
        return colDefs.map(colDef => {
            return { header: colDef.hasOwnProperty('headerName') ? colDef.headerName :  colDef.headerValueGetter() , key: colDef.field }; 
        })
    },

    getVisibleDataFromGrid(grid, formatValue = null){
        return grid.gridControl.getModel().rowsToDisplay.map(e => {
            if(typeof formatValue === 'function'){
                return formatValue.apply(null, [grid, e.data]);
            }
            return e.data;
        });
    },

    getExcelHeaderFromFilter(){
        return [
            {label: t('date'), value: this.getFilterInSession().DTMESCARD},
            {label: t('contract'), value: this.getFilterInSession().TPCONTRATO},
            {label: t('unit_code'), value: this.getFilterInSession().CDFILIAL},
            {label: t('unit_name'), value: this.getFilterInSession().NMFILIAL},
            {label: t('service_code'), value: this.getFilterInSession().CDSERVFILI},
            {label: t('service_name'), value: this.getFilterInSession().NMSERVICO}
        ];
    },
    
    spliceOnCondition(array, callback) {
        var i = array.length;
        while (i--) {
            if (callback(array[i], i)) {
                array.splice(i, 1);
            }
        }
    },

    arrayToOjbect(source){
        return source.reduce((acc, cur) => {
            let key = Object.keys(cur)[0];
            acc[key] = cur[key];
            return acc; 
        }, {})
    },

    deepCopy(source){
        return JSON.parse(JSON.stringify(source));
    },

    /**
     * @param {Promise<Array<any>>} dataFetchPromise 
     * @param {*} [defaultValue] a value to return when the filter is not selected 
     */
    ifFilterSelected(dataFetchPromise, defaultValue = []){
        const {CDFILIAL, CDSERVFILI, TPCONTRATO, DTMESCARD} = this.getFilterInSession();
        if(CDFILIAL && CDSERVFILI && TPCONTRATO && DTMESCARD){
            return dataFetchPromise();
        } else {
            return Promise.resolve(defaultValue);
        }
    },
    
    translateOptionsBunchCellEditor(obj){
  
        if(!obj.options){
            return obj;
        }
        
        let arrKeys;
        arrKeys = Object.keys(obj.options);
        
        for (let i =0; i<arrKeys.length; i++){
            
            let key = arrKeys[i];

            obj.options[key] = t(obj.options[key]);
            
            
        }
        return obj;
    },
    
    translateVSelectCellEditor(obj){
  
        if(!obj.options){
            return obj;
        }
        
        for (let i =0; i<obj.options.length; i++){
            
            obj.options[i]['label'] = t(obj.options[i]['label']);
            
        }
        
        //Tratativa de acordo com a função prepareColumnsDefs do arquivo /otm/client/src/components/src/GridComponent.vue 
        obj.jumpNextEditableCell = () => {};
       
        return obj;
        
    },
    
    translateValueFormatter(obj){
  
      
        let arrKeys;
        arrKeys = Object.keys(obj);
        
        for (let i =0; i<arrKeys.length; i++){
            
            let key = arrKeys[i];

            obj[key] = t(obj[key]);
            
            
        }
        return obj;
    },
    

    sortBy,
    debounce,
    insertChar,
    maskFactory,
    showConfirmationDialog,
    isInvalidNumber
}


